import React, { useState } from 'react';
import {
    TextInput,
    Radio,
    Textarea,
    Button,
    Group,
    Stack,
    Text,
    Paper,
    Avatar,
    Divider,
    Grid,
    Select,
    Alert,
    Title,
    Badge,
    Box,
} from '@mantine/core';
import { DateInput, DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconCalendar, IconBriefcase, IconBuildingSkyscraper, IconUser } from '@tabler/icons-react';

interface ExitDetailsFormProps {
    onClose: () => void;
    onSubmit: (values: any) => void;
    employeeData: any; // Add proper type if available
}

export const ExitDetailsForm: React.FC<ExitDetailsFormProps> = ({ onClose, onSubmit, employeeData }) => {
    const form = useForm({
        initialValues: {
            lastWorkingDay: null,
            reasonForExit: '',
            settlementOption: 'regular',
            customSettlementDate: null,
            personalEmail: '',
            notes: '',
        },
        validate: {
            lastWorkingDay: (value) => (value ? null : 'Last working day is required'),
            reasonForExit: (value) => (value ? null : 'Reason for exit is required'),
            personalEmail: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
            customSettlementDate: (value, values) => (values.settlementOption === 'custom' && !value ? 'Date is required' : null),
        },
    });

    const handleSubmit = (values: typeof form.values) => {
        onSubmit(values);
    };

    return (
        <Grid gutter="xl">
            {/* Left Column: Form */}
            <Grid.Col span={12} md={7}>
                <form onSubmit={form.onSubmit(handleSubmit)}>
                    <Stack spacing="md">
                        <Text color="dimmed" size="sm">Complete the exit formalities and settlement information</Text>

                        <DateInput
                            label="Last Working Day"
                            placeholder="dd-mm-yyyy"
                            required
                            rightSection={<IconCalendar size={16} />}
                            {...form.getInputProps('lastWorkingDay')}
                        />

                        <Select
                            label="Reason for Exit"
                            placeholder="Select a reason"
                            required
                            data={[
                                { value: 'resignation', label: 'Resignation' },
                                { value: 'termination', label: 'Termination' },
                                { value: 'retirement', label: 'Retirement' },
                                { value: 'other', label: 'Other' },
                            ]}
                            {...form.getInputProps('reasonForExit')}
                        />

                        <Radio.Group
                            label="Final Settlement Option"
                            required
                            {...form.getInputProps('settlementOption')}
                        >
                            <Stack mt="xs" spacing="sm">
                                <Paper p="sm" withBorder>
                                    <Radio value="regular" label="Pay as per the regular pay schedule" description="Settlement will be processed on the next scheduled payroll date" />
                                </Paper>
                                <Paper p="sm" withBorder>
                                    <Stack spacing={4}>
                                        <Radio value="custom" label="Pay on a given date" />
                                        {form.values.settlementOption === 'custom' && (
                                            <DateInput
                                                placeholder="dd-mm-yyyy"
                                                mt="xs"
                                                rightSection={<IconCalendar size={16} />}
                                                {...form.getInputProps('customSettlementDate')}
                                            />
                                        )}
                                    </Stack>
                                </Paper>
                            </Stack>
                        </Radio.Group>

                        <TextInput
                            label="Personal Email Address"
                            placeholder="example@email.com"
                            required
                            {...form.getInputProps('personalEmail')}
                        />

                        <Textarea
                            label="Notes"
                            placeholder="Add any additional notes or comments..."
                            minRows={4}
                            {...form.getInputProps('notes')}
                        />

                        <Alert icon={<IconAlertCircle size={16} />} title="Important" color="yellow" variant="light">
                            Portal is not enabled for this employee. Kindly collect the proof of investments before processing the payroll.
                        </Alert>

                        <Group position="right" mt="xl">
                            <Button variant="default" onClick={onClose} fullWidth style={{ flex: 1 }}>Cancel</Button>
                            <Button type="submit" color="red" fullWidth style={{ flex: 1 }}>Proceed</Button>
                        </Group>
                    </Stack>
                </form>
            </Grid.Col>

            {/* Right Column: Employee Details */}
            <Grid.Col span={12} md={5}>
                <Paper p="lg" radius="md" withBorder style={{ height: '100%', backgroundColor: '#fff' }}>
                    <Stack align="center" spacing="md">
                        <Avatar size={80} radius="xl" color="red">
                            {employeeData?.first_name?.[0]}{employeeData?.last_name?.[0]}
                        </Avatar>
                        <div style={{ textAlign: 'center' }}>
                            <Title order={4}>{employeeData?.first_name} {employeeData?.last_name}</Title>
                            <Text color="dimmed" size="sm">VTB-001</Text>
                        </div>

                        <Stack spacing="xs" w="100%">
                            <Paper p="sm" bg="gray.0">
                                <Group noWrap spacing="sm">
                                    <IconBriefcase size={20} color="gray" />
                                    <div>
                                        <Text size="xs" color="dimmed">Designation</Text>
                                        <Text size="sm" weight={500}>{employeeData?.designation || 'Developer'}</Text>
                                    </div>
                                </Group>
                            </Paper>
                            <Paper p="sm" bg="gray.0">
                                <Group noWrap spacing="sm">
                                    <IconBuildingSkyscraper size={20} color="gray" />
                                    <div>
                                        <Text size="xs" color="dimmed">Department</Text>
                                        <Text size="sm" weight={500}>{employeeData?.department || 'Development'}</Text>
                                    </div>
                                </Group>
                            </Paper>
                            <Paper p="sm" bg="gray.0">
                                <Group noWrap spacing="sm">
                                    <IconCalendar size={20} color="gray" />
                                    <div>
                                        <Text size="xs" color="dimmed">Date of Joining</Text>
                                        <Text size="sm" weight={500}>{employeeData?.doj || '01/11/2025'}</Text>
                                    </div>
                                </Group>
                            </Paper>
                        </Stack>

                        <Divider w="100%" my="sm" />

                        <Group position="apart" w="100%">
                            <Text size="sm" color="dimmed">Employment Status</Text>
                            <Badge color="orange" variant="light">Exit in Progress</Badge>
                        </Group>

                    </Stack>
                </Paper>
            </Grid.Col>
        </Grid>
    );
};
